'use client';

import React, { useState, useEffect } from 'react';
import { useUseCelo } from '../../providers/UseCeloProvider';
import type { SubscriptionDetails } from '../../types/payment';

// Minimal props
interface SimplifiedSubscriptionManagerProps {
  theme?: 'light' | 'dark';
  onCancelComplete?: () => void;
  onPlanChangeComplete?: (newPlanId: string) => void;
  onError?: (error: Error) => void;
  className?: string;
}

interface Plan {
  id: string;
  name: string;
  description?: string;
  pricing?: unknown;
  stripePriceId?: string;
}

/**
 * SimplifiedSubscriptionManager - Minimal subscription widget for end users
 *
 * Features:
 * - View current subscription
 * - Change plans (redirects to checkout)
 * - Cancel subscription (immediate)
 *
 * @example
 * ```tsx
 * <SimplifiedSubscriptionManager
 *   onCancelComplete={() => console.log('Canceled!')}
 * />
 * ```
 */
export function SimplifiedSubscriptionManager({
  theme = 'light',
  onCancelComplete,
  onPlanChangeComplete,
  onError,
  className = '',
}: SimplifiedSubscriptionManagerProps = {}): React.ReactElement {

  const {
    loading: contextLoading,
    appId,
    publicKey,
    apiHost,
    getSubscription,
    createCheckout,
    cancelSubscription,
    pauseSubscription,
    resumeSubscription,
  } = useUseCelo();

  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // Fetch subscription
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!getSubscription) return;

      setIsLoadingSubscription(true);
      try {
        const data = await getSubscription();
        console.log('✅ Fetched subscription data:', data);

        // The API returns { user: {...}, subscription: {...} }
        if (data) {
          setSubscription(data.subscription);

          // Extract userId from user.externalId
          if (data.user?.externalId) {
            setUserId(data.user.externalId);
          }
        }
      } catch (err) {
        console.error('❌ Failed to fetch subscription:', err);
        setError(err instanceof Error ? err.message : 'Failed to load subscription');
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    fetchSubscription();
  }, [getSubscription]);

  // Fetch available plans
  useEffect(() => {
    const fetchPlans = async () => {
      if (!appId || !publicKey || !apiHost) return;

      setIsLoadingPlans(true);
      try {
        const response = await fetch(`${apiHost}/api/public/plans/${appId}`, {
          headers: {
            'x-api-key': publicKey,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setPlans(data.data || data.plans || []);
        }
      } catch (err) {
        console.error('Failed to fetch plans:', err);
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchPlans();
  }, [appId, publicKey, apiHost]);

  // Handle plan change (direct API call to change-plan endpoint)
  const handlePlanChange = async (planId: string) => {
    if (!userId || !appId || !apiHost || !publicKey) {
      setError('Unable to change plan. Please try again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Call the change-plan API endpoint directly
      const response = await fetch(`${apiHost}/api/public/subscriptions/change-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': publicKey,
        },
        body: JSON.stringify({
          externalId: userId,
          newPlanId: planId,
          immediate: true, // Apply change immediately
        }),
      });

      const data = await response.json();
      console.log('📥 Change plan response:', data);

      // Check if checkout is required (no payment method on file)
      if (data.requiresCheckout) {
        console.log('💳 Checkout required, redirecting to payment...');

        // Redirect to checkout with the new plan
        if (createCheckout) {
          const session = await createCheckout({
            planId,
            userId,
            successUrl: `${window.location.origin}/checkout/success`,
            cancelUrl: `${window.location.origin}/pricing`,
          });

          if (session.url) {
            window.location.href = session.url;
          } else {
            throw new Error('Failed to create checkout session');
          }
        } else {
          throw new Error('Please add a payment method to change your plan');
        }
        return;
      }

      if (!response.ok || !data.success) {
        const errorMsg = data.details || data.error || 'Failed to change plan';
        console.error('❌ Change plan failed:', errorMsg);
        throw new Error(errorMsg);
      }

      // Refresh subscription data
      const updatedData = await getSubscription?.();
      if (updatedData) {
        setSubscription(updatedData.subscription);
      }

      // Call callback
      onPlanChangeComplete?.(planId);

      // Show success message and close modal
      setSuccessMessage('Plan changed successfully!');
      setShowPlanSelector(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to change plan';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle immediate cancellation
  const handleCancel = async () => {
    if (!cancelSubscription || !userId) {
      setError('Cancel functionality not available');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Immediate cancellation
      await cancelSubscription({
        userId,
        immediate: true,
        cancelAtPeriodEnd: false
      });

      setSuccessMessage('Your subscription has been canceled successfully.');
      setShowCancelConfirm(false);
      onCancelComplete?.();

      // Reload to show updated status
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel subscription';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle pause
  const handlePause = async () => {
    if (!pauseSubscription || !userId) {
      setError('Pause functionality not available');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const pauseUntil = new Date();
      pauseUntil.setMonth(pauseUntil.getMonth() + 1);
      await pauseSubscription({ userId, behavior: 'void' });
      setSuccessMessage('Subscription paused for 1 month');
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to pause subscription';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle resume
  const handleResume = async () => {
    if (!resumeSubscription || !userId) {
      setError('Resume functionality not available');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      await resumeSubscription(userId);
      setSuccessMessage('Subscription resumed successfully');
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resume subscription';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsProcessing(false);
    }
  };

  const isDark = theme === 'dark';
  const baseClasses = isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900';
  const borderClass = isDark ? 'border-gray-700' : 'border-gray-200';

  // Helper function to format pricing display
  const getPricingDisplay = (pricing: any): { amount: string; interval: string } => {
    if (!pricing || typeof pricing !== "object") {
      return { amount: "Free", interval: "" };
    }

    const pricingConfig = pricing as any;
    const currency = pricingConfig.currency || "USD";
    const currencySymbol = currency === "USD" ? "$" : currency;

    // Check for free plan
    if (pricingConfig.model === "free") {
      return { amount: "Free", interval: "" };
    }

    // Usage-based pricing
    if (pricingConfig.model === "usage" || pricingConfig.model === "usage_based") {
      const basePrice = pricingConfig.usage?.tiers?.[0]?.price || pricingConfig.baseFee || 0;
      return {
        amount: basePrice === 0 ? "Free" : `Starting at ${currencySymbol}${basePrice}`,
        interval: basePrice === 0 ? "" : `/${pricingConfig.usage?.unit || "unit"}`
      };
    }

    // Get the price amount
    const amount = pricingConfig.amount ?? pricingConfig.baseFee ?? pricingConfig.price ?? 0;

    if (amount === 0) {
      return { amount: "Free", interval: "" };
    }

    // Get the billing interval
    const interval = pricingConfig.interval || pricingConfig.billingCycle || "month";

    return {
      amount: `${currencySymbol}${amount}`,
      interval: `/${interval}`
    };
  };

  // Loading state
  if (contextLoading || isLoadingPlans) {
    return (
      <div className={className} style={{
        padding: '32px',
        borderRadius: '16px',
        background: isDark ? '#1f2937' : '#ffffff',
        color: isDark ? '#ffffff' : '#111827',
        border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ height: '32px', background: isDark ? '#374151' : '#e5e7eb', borderRadius: '8px', width: '60%', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
          <div style={{ height: '20px', background: isDark ? '#374151' : '#e5e7eb', borderRadius: '6px', width: '40%', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
          <div style={{ height: '20px', background: isDark ? '#374151' : '#e5e7eb', borderRadius: '6px', width: '50%', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
          <div style={{ height: '48px', background: isDark ? '#374151' : '#e5e7eb', borderRadius: '8px', width: '100%', marginTop: '8px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
        </div>
      </div>
    );
  }

  // No subscription state
  if (!subscription) {
    return (
      <div className={className} style={{
        padding: '32px',
        borderRadius: '16px',
        background: isDark ? '#1f2937' : '#ffffff',
        color: isDark ? '#ffffff' : '#111827',
        border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        maxWidth: '800px',
        margin: '0 auto',
        textAlign: 'center' as const
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: isDark ? '#374151' : '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          fontSize: '28px'
        }}>
          📋
        </div>
        <h3 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px', color: isDark ? '#ffffff' : '#111827' }}>No Active Subscription</h3>
        <p style={{ fontSize: '16px', marginBottom: '24px', color: isDark ? '#9ca3af' : '#6b7280', maxWidth: '400px', margin: '0 auto 32px' }}>
          You don't have an active subscription yet. Choose a plan to get started.
        </p>
        <button
          onClick={() => setShowPlanSelector(true)}
          style={{
            padding: '14px 32px',
            background: '#2563eb',
            color: '#ffffff',
            borderRadius: '10px',
            border: 'none',
            fontWeight: 600,
            fontSize: '16px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          View Plans
        </button>
      </div>
    );
  }

  const statusColors = {
    active: 'bg-green-100 text-green-800 border-green-200',
    trialing: 'bg-blue-100 text-blue-800 border-blue-200',
    canceled: 'bg-red-100 text-red-800 border-red-200',
    paused: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  };

  const statusColor = statusColors[subscription.status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800 border-gray-200';

  return (
    <div className={className} style={{
      padding: '32px',
      borderRadius: '16px',
      background: isDark ? '#1f2937' : '#ffffff',
      color: isDark ? '#ffffff' : '#111827',
      border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: isDark ? '#ffffff' : '#111827', letterSpacing: '-0.025em' }}>Your Subscription</h2>

        {/* Success Message */}
        {successMessage && (
          <div style={{
            background: '#d1fae5',
            border: '1px solid #a7f3d0',
            color: '#065f46',
            padding: '14px 18px',
            borderRadius: '10px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '15px',
            fontWeight: 500
          }}>
            <span style={{ fontSize: '20px' }}>✓</span>
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            padding: '14px 18px',
            borderRadius: '10px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '15px',
            fontWeight: 500
          }}>
            <span style={{ fontSize: '20px' }}>⚠</span>
            {error}
          </div>
        )}
      </div>

      {/* Current Plan Card */}
      <div style={{
        marginBottom: '28px',
        padding: '28px',
        border: `2px solid ${isDark ? '#374151' : '#e5e7eb'}`,
        borderRadius: '12px',
        background: isDark ? 'rgba(31, 41, 55, 0.5)' : 'rgba(249, 250, 251, 0.5)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h3 style={{ fontSize: '24px', fontWeight: 700, color: isDark ? '#ffffff' : '#111827', marginBottom: '8px' }}>{subscription.planName || 'Current Plan'}</h3>
            <p style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280', fontFamily: 'monospace' }}>
              {subscription.planId}
            </p>
          </div>
          <span style={{
            padding: '6px 16px',
            borderRadius: '100px',
            fontSize: '13px',
            fontWeight: 700,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.05em',
            border: '2px solid',
            background: subscription.status === 'active' ? '#d1fae5' : subscription.status === 'trialing' ? '#dbeafe' : subscription.status === 'canceled' ? '#fee2e2' : '#fef3c7',
            color: subscription.status === 'active' ? '#065f46' : subscription.status === 'trialing' ? '#1e40af' : subscription.status === 'canceled' ? '#991b1b' : '#92400e',
            borderColor: subscription.status === 'active' ? '#10b981' : subscription.status === 'trialing' ? '#3b82f6' : subscription.status === 'canceled' ? '#ef4444' : '#f59e0b'
          }}>
            {subscription.status || 'Active'}
          </span>
        </div>

        {/* Dates */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '15px' }}>
          {subscription.currentPeriodEnd && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              background: isDark ? '#374151' : '#f9fafb',
              borderRadius: '8px'
            }}>
              <span style={{ fontSize: '18px' }}>📅</span>
              <div>
                <span style={{ fontWeight: 600, color: isDark ? '#ffffff' : '#111827' }}>
                  {subscription.status === 'canceled' ? 'Access until' : 'Renews on'}
                </span>
                <span style={{ marginLeft: '8px', color: isDark ? '#9ca3af' : '#6b7280' }}>
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>
          )}

          {subscription.trialEnd && subscription.status === 'trialing' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              background: '#dbeafe',
              borderRadius: '8px',
              border: '1px solid #93c5fd'
            }}>
              <span style={{ fontSize: '18px' }}>🎁</span>
              <div>
                <span style={{ fontWeight: 600, color: '#1e40af' }}>
                  Trial ends
                </span>
                <span style={{ marginLeft: '8px', color: '#1e40af' }}>
                  {new Date(subscription.trialEnd).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {subscription.status === 'active' && (
          <>
            <button
              onClick={() => setShowPlanSelector(true)}
              disabled={isProcessing}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: '#2563eb',
                color: '#ffffff',
                borderRadius: '10px',
                border: 'none',
                fontWeight: 600,
                fontSize: '16px',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.5 : 1,
                transition: 'all 0.2s',
                boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
              }}
              onMouseOver={(e) => !isProcessing && (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Change Plan
            </button>

            {pauseSubscription && (
              <button
                onClick={handlePause}
                disabled={isProcessing}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: isDark ? '#374151' : '#ffffff',
                  color: isDark ? '#ffffff' : '#111827',
                  border: `2px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '16px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => !isProcessing && (e.currentTarget.style.borderColor = isDark ? '#6b7280' : '#9ca3af')}
                onMouseOut={(e) => e.currentTarget.style.borderColor = isDark ? '#4b5563' : '#d1d5db'}
              >
                {isProcessing ? 'Processing...' : 'Pause Subscription'}
              </button>
            )}

            <button
              onClick={() => setShowCancelConfirm(true)}
              disabled={isProcessing}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: 'transparent',
                color: '#dc2626',
                border: '2px solid #dc2626',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '16px',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.5 : 1,
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => !isProcessing && (e.currentTarget.style.background = '#fef2f2')}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              Cancel Subscription
            </button>
          </>
        )}

        {subscription.status === 'trialing' && (
          <>
            <button
              onClick={() => setShowPlanSelector(true)}
              disabled={isProcessing}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: '#2563eb',
                color: '#ffffff',
                borderRadius: '10px',
                border: 'none',
                fontWeight: 600,
                fontSize: '16px',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.5 : 1,
                transition: 'all 0.2s',
                boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
              }}
              onMouseOver={(e) => !isProcessing && (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Change Plan
            </button>

            <button
              onClick={() => setShowCancelConfirm(true)}
              disabled={isProcessing}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: 'transparent',
                color: '#dc2626',
                border: '2px solid #dc2626',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '16px',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.5 : 1,
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => !isProcessing && (e.currentTarget.style.background = '#fef2f2')}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              Cancel Trial
            </button>
          </>
        )}

        {(subscription.status as string) === 'paused' && resumeSubscription && (
          <button
            onClick={handleResume}
            disabled={isProcessing}
            style={{
              width: '100%',
              padding: '14px 20px',
              background: '#16a34a',
              color: '#ffffff',
              borderRadius: '10px',
              border: 'none',
              fontWeight: 600,
              fontSize: '16px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              opacity: isProcessing ? 0.5 : 1,
              transition: 'all 0.2s',
              boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.3)'
            }}
            onMouseOver={(e) => !isProcessing && (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            {isProcessing ? 'Resuming...' : 'Resume Subscription'}
          </button>
        )}

        {subscription.status === 'canceled' && (
          <button
            onClick={() => setShowPlanSelector(true)}
            style={{
              width: '100%',
              padding: '14px 20px',
              background: '#2563eb',
              color: '#ffffff',
              borderRadius: '10px',
              border: 'none',
              fontWeight: 600,
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Resubscribe
          </button>
        )}
      </div>

      {/* Plan Selector Modal */}
      {showPlanSelector && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px'
        }}>
          <div style={{
            background: isDark ? '#1f2937' : '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ padding: '32px' }}>
              <h3 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '24px', color: isDark ? '#ffffff' : '#111827' }}>Choose a Plan</h3>

              {isProcessing && (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px 20px',
                  background: '#dbeafe',
                  border: '1px solid #93c5fd',
                  color: '#1e40af',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontSize: '15px',
                  fontWeight: 500
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #3b82f6',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span>Processing plan change...</span>
                </div>
              )}

              {plans.length === 0 ? (
                <p style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '16px' }}>No plans available</p>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  {plans.map((plan) => {
                    const isCurrentPlan = plan.id === subscription.planId;
                    const pricing = getPricingDisplay(plan.pricing);
                    return (
                      <div
                        key={plan.id}
                        onClick={() => !isCurrentPlan && !isProcessing && handlePlanChange(plan.id)}
                        style={{
                          border: `2px solid ${isCurrentPlan ? '#2563eb' : (isDark ? '#374151' : '#e5e7eb')}`,
                          borderRadius: '12px',
                          padding: '24px',
                          cursor: isProcessing ? 'not-allowed' : (isCurrentPlan ? 'default' : 'pointer'),
                          opacity: isProcessing ? 0.5 : 1,
                          background: isCurrentPlan ? (isDark ? 'rgba(37, 99, 235, 0.1)' : '#eff6ff') : (isDark ? '#1f2937' : '#ffffff'),
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => !isCurrentPlan && !isProcessing && (e.currentTarget.style.borderColor = '#3b82f6')}
                        onMouseOut={(e) => !isCurrentPlan && (e.currentTarget.style.borderColor = isDark ? '#374151' : '#e5e7eb')}
                      >
                        <div style={{ marginBottom: '16px' }}>
                          <h4 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '4px', color: isDark ? '#ffffff' : '#111827' }}>{plan.name}</h4>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                            <span style={{ fontSize: '32px', fontWeight: 700, color: isDark ? '#ffffff' : '#111827' }}>{pricing.amount}</span>
                            {pricing.interval && (
                              <span style={{ fontSize: '16px', color: isDark ? '#9ca3af' : '#6b7280' }}>{pricing.interval}</span>
                            )}
                          </div>
                        </div>
                        {plan.description && (
                          <p style={{ fontSize: '14px', marginBottom: '12px', color: isDark ? '#9ca3af' : '#6b7280', lineHeight: '1.5' }}>
                            {plan.description}
                          </p>
                        )}
                        {isCurrentPlan && (
                          <span style={{
                            display: 'inline-block',
                            fontSize: '14px',
                            color: '#2563eb',
                            fontWeight: 600
                          }}>
                            ✓ Current Plan
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => setShowPlanSelector(false)}
                disabled={isProcessing}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: isDark ? '#374151' : '#ffffff',
                  color: isDark ? '#ffffff' : '#111827',
                  border: `2px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '16px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => !isProcessing && (e.currentTarget.style.background = isDark ? '#4b5563' : '#f3f4f6')}
                onMouseOut={(e) => e.currentTarget.style.background = isDark ? '#374151' : '#ffffff'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px'
        }}>
          <div style={{
            background: isDark ? '#1f2937' : '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxWidth: '500px',
            width: '100%'
          }}>
            <div style={{ padding: '32px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '32px'
              }}>
                ⚠️
              </div>
              <h3 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '16px', color: isDark ? '#ffffff' : '#111827', textAlign: 'center' }}>Cancel Subscription?</h3>
              <p style={{
                fontSize: '16px',
                marginBottom: '32px',
                color: isDark ? '#d1d5db' : '#4b5563',
                textAlign: 'center',
                lineHeight: '1.6'
              }}>
                Your subscription will be canceled immediately and you'll lose access right away.
                This action cannot be undone.
              </p>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleCancel}
                  disabled={isProcessing}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    background: '#dc2626',
                    color: '#ffffff',
                    borderRadius: '10px',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '16px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.5 : 1,
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => !isProcessing && (e.currentTarget.style.background = '#b91c1c')}
                  onMouseOut={(e) => e.currentTarget.style.background = '#dc2626'}
                >
                  {isProcessing ? 'Canceling...' : 'Yes, Cancel Now'}
                </button>
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={isProcessing}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    background: isDark ? '#374151' : '#ffffff',
                    color: isDark ? '#ffffff' : '#111827',
                    border: `2px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '16px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => !isProcessing && (e.currentTarget.style.background = isDark ? '#4b5563' : '#f3f4f6')}
                  onMouseOut={(e) => e.currentTarget.style.background = isDark ? '#374151' : '#ffffff'}
                >
                  Keep It
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
